import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import UniversitiesMarquee from "@/components/UniversitiesMarquee";
import AchievementsRoller from "@/components/AchievementsRoller";
import GenZHook from "@/components/GenZHook";
import Destinations from "@/components/Destinations";
import Story from "@/components/Story";
import CrewSection from "@/components/CrewSection";
import RealTalkSection from "@/components/RealTalkSection";
import Services from "@/components/Services";
import WorldMap from "@/components/WorldMap";
import ProblemSolutionToggle from "@/components/ProblemSolutionToggle";
import AlumniShowcase from "@/components/AlumniShowcase";
import FutureYou from "@/components/FutureYou";
import SuccessStudents from "@/components/SuccessStudents";
import UrgencyBanner from "@/components/UrgencyBanner";
import Inquiry from "@/components/Inquiry";
import GoogleMapsReviews from "@/components/GoogleMapsReviews";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Which is the best study abroad consultant in Nagpur?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Shah Overseas Education is widely regarded as one of the leading study abroad consultancies in Nagpur, India — 10+ years in business, 45,000+ students placed, 350+ partner universities, and $1M+ in scholarships secured for students annually. We offer end-to-end counselling for USA, UK, Canada, Australia, Ireland, Europe, NZ & Dubai.",
      },
    },
    {
      "@type": "Question",
      name: "Does Shah Overseas offer GRE and IELTS coaching in Nagpur?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Shah Overseas runs an in-house Test Prep Academy in Nagpur offering live classroom and online coaching for GRE, GMAT, IELTS, TOEFL, PTE and Duolingo English Test. All classes are taught by top-percentile educators (GRE 330+, GMAT 730+, IELTS 8.5) with small batches of ≤15 students and a score-boost guarantee.",
      },
    },
    {
      "@type": "Question",
      name: "What is the cost of studying abroad from India?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Total cost varies by country and course: USA ₹70 lakh – 1.4 crore (2-year MS), UK ₹35-65 lakh (1-year MSc), Canada ₹45-75 lakh (2-year program), Australia ₹55-90 lakh (2-year Master's), Ireland ₹25-42 lakh (1-year MSc), Germany ₹18-30 lakh (low/no tuition), Dubai ₹25-50 lakh. Scholarships and education-loan assistance can reduce out-of-pocket cost by 30-100%.",
      },
    },
    {
      "@type": "Question",
      name: "How do I start the process to study abroad from Nagpur?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Start with a free 30-minute counselling session at Shah Overseas Education in Nagpur (Plot 20, North Ambazari Road) or online via video call. We map your goals, evaluate your profile, plan tests (GRE/GMAT/IELTS), shortlist universities (Reach/Match/Safety), draft SOPs & LORs, hunt scholarships and education loans, prep your visa interview, and support pre-departure logistics. The full timeline from inquiry to boarding is typically 12-15 months.",
      },
    },
    {
      "@type": "Question",
      name: "Is Shah Overseas Education present in Pune as well?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. While our head office is in Nagpur (Plot 20, North Ambazari Road), Shah Overseas Education serves students from Pune, Mumbai, Nashik, Aurangabad and across Maharashtra through online counselling, weekend Pune meet-ups and a growing local presence. Students from Pune can book free video counselling, attend live test-prep classes online, and visit our Nagpur campus for in-person profile reviews.",
      },
    },
  ],
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#282829]">
      <Helmet>
        <title>Shah Overseas Education | Best Study Abroad Consultants in Nagpur, India</title>
        <meta name="description" content="Top study abroad consultants in Nagpur, India. 45,000+ students placed at 350+ universities (USA, UK, Canada, Australia, Ireland, Europe, Dubai). GRE, GMAT, IELTS, TOEFL, PTE & DET coaching. Book your free counselling today." />
        <link rel="canonical" href="https://www.shahoverseasedu.com/" />
        <meta property="og:title" content="Shah Overseas Education | Best Study Abroad Consultants in Nagpur" />
        <meta property="og:description" content="India's trusted study abroad consultancy since 2014. Free counselling, test prep, visa guidance — all under one roof in Nagpur." />
        <meta property="og:url" content="https://www.shahoverseasedu.com/" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <Navbar />
      <main>
        <Hero />

        {/* SEO-rich intro paragraph (search-visible) */}
        <section
          aria-label="About Shah Overseas Education"
          className="bg-white border-b-[3px] border-[#282829]"
          data-testid="seo-intro" className="sr-only"
        >
          <div className="max-w-5xl mx-auto px-6 sm:px-10 py-8 md:py-10 text-center">
            <p className="text-sm md:text-base text-[#282829]/75 leading-relaxed">
              <b className="sr-only">Shah Overseas Education</b> is one of the most trusted{" "}
              <b>study abroad consultants in Nagpur, Maharashtra</b> — guiding Indian students
              into <b>top universities in the USA, UK, Canada, Australia, Ireland, Germany, Europe & Dubai</b> since 2014.
              With <b>45,000+ students placed</b> across <b>350+ partner universities</b> and <b>$1M+ scholarships</b> unlocked,
              we cover every step in-house: from <b>GRE, GMAT, IELTS, TOEFL, PTE & Duolingo coaching in Nagpur</b> to SOP/LOR crafting,
              scholarship hunting, student-visa counselling & pre-departure support — all hands-on, ethical, and outcome-driven.
              <b className="text-[#282829]"> Book your free counselling today</b> and discover the destination, course and timeline that fits your story.
            </p>
          </div>
        </section>

        <UniversitiesMarquee />
        <AchievementsRoller />
        <GenZHook />
        <Destinations />
        <ProblemSolutionToggle />
        <WorldMap />
        <Story />
        <CrewSection />
        <RealTalkSection />
        <Services />
        <AlumniShowcase />
        <FutureYou />
        <SuccessStudents />
        <UrgencyBanner />
        <Inquiry />
        <GoogleMapsReviews />
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
